import React from 'react';
import { Bars3BottomRightIcon } from '@heroicons/react/24/outline';
import ChatAvatar from './ChatAvatar';
import RecipientPicker from './RecipientPicker';
import type { ChatRoom, ChatRoomMember, TenantMember } from '../../types/chat';
import { useCurrentChatIdentity } from '../../hooks/chat/useCurrentChatIdentity';
import { useImageUrl } from '../../services/uploads';

interface ChatHeaderProps {
  // When `composing` is true, render an inline recipient picker
  // (new chat flow) instead of the room banner.
  composing?: boolean;
  pickerSelection?: TenantMember[];
  onPickerChange?: (members: TenantMember[]) => void;
  pickerMulti?: boolean;
  onCancelCompose?: () => void;

  // When `room` is set and `composing` is false, render the active room
  // banner (centered peer for direct, avatar group for group).
  room?: ChatRoom;

  onTogglePanel?: () => void;
  panelOpen?: boolean;
}

const HEADER_HEIGHT = 72;
const AVATAR_SIZE = 32;
const MAX_AVATARS = 3;

interface OverlapAvatarProps {
  src?: string | null;
  name?: string | null;
  zIndex: number;
}

const OverlapAvatar: React.FC<OverlapAvatarProps> = ({ src, name, zIndex }) => {
  const [resolved] = useImageUrl(src ?? null);
  const initials = (() => {
    if (!name) return '·';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  })();
  return (
    <div
      className="relative shrink-0 -mr-1.5 rounded-full ring-2 ring-white dark:ring-neutral-900"
      style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, zIndex }}
    >
      {resolved ? (
        <img
          src={resolved}
          alt={name ?? ''}
          className="w-full h-full rounded-full object-cover"
        />
      ) : (
        <div
          className="w-full h-full rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center text-primary-700 dark:text-primary-200 font-semibold"
          style={{ fontSize: 12 }}
        >
          {initials}
        </div>
      )}
    </div>
  );
};

const PlusMoreChip: React.FC<{ count: number; zIndex: number }> = ({ count, zIndex }) => (
  <div
    className="relative shrink-0 -mr-1.5 rounded-full ring-2 ring-white dark:ring-neutral-900 bg-primary-200 dark:bg-primary-800/60 flex items-center justify-center text-primary-800 dark:text-primary-200 text-xs font-semibold"
    style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, zIndex }}
  >
    +{count}
  </div>
);

const AvatarGroup: React.FC<{ members: ChatRoomMember[] }> = ({ members }) => {
  const visible = members.slice(0, MAX_AVATARS);
  const overflow = Math.max(0, members.length - visible.length);
  // Per Figma: leftmost avatar has the highest z-index (sits on top),
  // overflow chip sits at the back.
  return (
    <div className="flex items-center pr-1.5">
      {visible.map((m, idx) => (
        <OverlapAvatar
          key={m.user_id}
          src={m.avatar_url}
          name={m.full_name ?? m.email}
          zIndex={MAX_AVATARS + 1 - idx}
        />
      ))}
      {overflow > 0 && <PlusMoreChip count={overflow} zIndex={1} />}
    </div>
  );
};

const ChatHeader: React.FC<ChatHeaderProps> = ({
  composing,
  pickerSelection,
  onPickerChange,
  pickerMulti,
  onCancelCompose,
  room,
  onTogglePanel,
  panelOpen,
}) => {
  const { userId } = useCurrentChatIdentity();

  // ── Centered content (varies by mode) ──────────────────────────────────
  let centerContent: React.ReactNode = null;

  if (composing) {
    // Composing mode: left-aligned "To: …" recipient picker per Figma
    // `6138-71319`. The header itself does NOT clip overflow — the
    // recipient dropdown floats out of the header bottom edge.
    return (
      <header
        className="relative z-30 shrink-0 border-b border-border-secondary"
        style={{ minHeight: HEADER_HEIGHT }}
      >
        {/* Gradient image is clipped to its own wrapper so it can't bleed
            outside the header even when overflow is allowed at the header
            level (needed so the dropdown escapes). */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            aria-hidden="true"
            className="brand-gradient absolute inset-0 opacity-50"
          />
        </div>
        <div className="relative h-full flex items-center pl-5 pr-2">
          <RecipientPicker
            selected={pickerSelection ?? []}
            onChange={onPickerChange ?? (() => {})}
            multi={pickerMulti ?? true}
            autoFocus
            onCancel={onCancelCompose}
          />
        </div>
      </header>
    );
  } else if (room) {
    if (room.type === 'group') {
      const selfUpper = (userId ?? '').toUpperCase();
      const others = room.members.filter(
        (m) => (m.user_id ?? '').toUpperCase() !== selfUpper
      );
      const groupMembers = others.length > 0 ? others : room.members;
      centerContent = <AvatarGroup members={groupMembers} />;
    } else {
      // Prefer the backend-computed peer (room.display_name) because it's
      // calculated server-side from the caller's JWT and is immune to any
      // client-side identity drift. Fall back to the local find if absent.
      const peer = room.members.find(
        (m) => (m.user_id ?? '').toUpperCase() !== (userId ?? '').toUpperCase()
      );
      const display =
        room.display_name ?? peer?.full_name ?? peer?.email ?? 'Direct message';
      const avatarSrc = room.display_avatar ?? peer?.avatar_url;
      const status = peer?.is_online ? 'Online' : peer?.role_name ?? '';
      centerContent = (
        <div className="flex items-center gap-3 max-w-full">
          <ChatAvatar
            src={avatarSrc}
            name={display}
            size={AVATAR_SIZE}
            online={peer?.is_online}
          />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-text-primary truncate">{display}</p>
            {status && (
              <p className="text-[11px] text-text-tertiary truncate">{status}</p>
            )}
          </div>
        </div>
      );
    }
  }

  return (
    <header
      className="relative shrink-0 border-b border-border-secondary"
      style={{ height: HEADER_HEIGHT }}
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
            aria-hidden="true"
            className="brand-gradient absolute inset-0 opacity-50"
          />
      </div>
      <div className="relative h-full grid grid-cols-[1fr_auto_1fr] items-center px-4 gap-2">
        {/* Left spacer keeps centered content actually centered. */}
        <div />
        <div className="flex items-center justify-center">{centerContent}</div>
        <div className="flex items-center gap-1 justify-self-end">
          {!composing && room && onTogglePanel && (
            <button
              type="button"
              onClick={onTogglePanel}
              aria-label={panelOpen ? 'Hide details' : 'Show details'}
              aria-pressed={panelOpen}
              className={`p-2 rounded-full transition-colors ${
                panelOpen
                  ? 'text-primary-700 dark:text-primary-300 bg-white/60 dark:bg-neutral-900/60'
                  : 'text-text-secondary hover:bg-white/50 dark:hover:bg-neutral-900/40'
              }`}
            >
              <Bars3BottomRightIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

export default ChatHeader;
